import { Controller } from "@hotwired/stimulus"
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
    static targets = ["input", "editor", "button", "fileInput"]
    static values = {
        content: String,
        directUploadUrl: { type: String, default: '/rails/active_storage/direct_uploads' }
    }

    connect() {
        this.editor = new Editor({
            element: this.editorTarget,
            extensions: [
                StarterKit,
                Image.configure({
                    HTMLAttributes: {
                        class: 'rounded-md max-w-full my-4',
                    },
                }),
                Link.configure({
                    openOnClick: false,
                    HTMLAttributes: {
                        class: 'text-beige-700 underline hover:text-beige-900',
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    },
                }),
            ],
            editorProps: {
                attributes: {
                    class: 'min-h-[200px] outline-none prose-sm sm:prose max-w-none',
                },
            },
            content: this.inputTarget.value || this.contentValue || '',
            onUpdate: () => {
                this.inputTarget.value = this.editor.getHTML()
                this.updateActiveButtons()
            },
            onSelectionUpdate: () => {
                this.updateActiveButtons()
            },
            onTransaction: () => {
                this.updateActiveButtons()
            }
        })
    }

    disconnect() {
        this.editor.destroy()
    }

    // Formatting actions
    toggleBold(event) { event.preventDefault(); this.editor.chain().focus().toggleBold().run() }
    toggleItalic(event) { event.preventDefault(); this.editor.chain().focus().toggleItalic().run() }
    toggleStrike(event) { event.preventDefault(); this.editor.chain().focus().toggleStrike().run() }
    toggleH1(event) { event.preventDefault(); this.editor.chain().focus().toggleHeading({ level: 1 }).run() }
    toggleH2(event) { event.preventDefault(); this.editor.chain().focus().toggleHeading({ level: 2 }).run() }
    toggleH3(event) { event.preventDefault(); this.editor.chain().focus().toggleHeading({ level: 3 }).run() }
    toggleBulletList(event) { event.preventDefault(); this.editor.chain().focus().toggleBulletList().run() }
    toggleOrderedList(event) { event.preventDefault(); this.editor.chain().focus().toggleOrderedList().run() }
    toggleBlockquote(event) { event.preventDefault(); this.editor.chain().focus().toggleBlockquote().run() }
    toggleCodeBlock(event) { event.preventDefault(); this.editor.chain().focus().toggleCodeBlock().run() }

    setLink(event) {
        event.preventDefault()
        const previousUrl = this.editor.getAttributes('link').href
        const url = window.prompt('URL', previousUrl)

        // cancelled
        if (url === null) {
            return
        }

        // empty
        if (url === '') {
            this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
            return
        }

        // update link
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run()
    }

    unsetLink(event) {
        event.preventDefault()
        this.editor.chain().focus().unsetLink().run()
    }

    attachFile(event) {
        event.preventDefault()
        this.fileInputTarget.click()
    }

    handleFileSelect(event) {
        const files = event.target.files
        if (files.length === 0) return

        Array.from(files).forEach(file => this.uploadFile(file))
        event.target.value = '' // reset input
    }

    uploadFile(file) {
        // Optional: show some loading state or placeholder here
        const upload = new DirectUpload(file, this.directUploadUrlValue)

        upload.create((error, blob) => {
            if (error) {
                console.error("Error uploading file", error)
                alert('Upload failed: ' + error)
                return
            }

            const fileUrl = `/rails/active_storage/blobs/redirect/${blob.signed_id}/${blob.filename}`

            if (file.type.startsWith('image/')) {
                this.editor.chain().focus().setImage({ src: fileUrl }).run()
            } else {
                this.editor.chain().focus().insertContent(`<a href="${fileUrl}" target="_blank" rel="noopener noreferrer">${file.name}</a>`).run()
            }
        })
    }

    updateActiveButtons() {
        if (!this.hasButtonTarget) return

        this.buttonTargets.forEach(btn => {
            const action = btn.dataset.actionName
            const level = btn.dataset.level

            let isActive = false
            if (level) {
                isActive = this.editor.isActive(action, { level: parseInt(level) })
            } else {
                isActive = this.editor.isActive(action)
            }

            if (isActive) {
                btn.classList.add('bg-gray-200', 'text-gray-900', 'ring-2', 'ring-gray-300')
                btn.classList.remove('text-gray-600')
            } else {
                btn.classList.remove('bg-gray-200', 'text-gray-900', 'ring-2', 'ring-gray-300')
                btn.classList.add('text-gray-600')
            }
        })
    }
}
